import { authReducer } from '@bitstopco/bitstop-theme';

import { useAppSelector } from '@/store';
import { useGetUserQuery } from '@/store/api/services/users/users';

interface UserDisplayProps {
  userId?: string | null;
  fallback?: string;
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Resolves a numeric (ergot) user id to the user's full name. Non-numeric ids ("system", emails, ...)
 * are rendered as-is, and the raw id is shown until the user loads.
 */
const UserDisplay = ({ userId, fallback = '-' }: UserDisplayProps) => {
  const currentUser = useAppSelector(authReducer.selectors.userInfo);
  const isNumericId = !!userId && !Number.isNaN(Number(userId));
  // Ergot's users/:id 404s for users with only hidden roles -- possibly the requester
  // themselves -- so the signed-in user is resolved from their own userinfo instead.
  const isCurrentUser = isNumericId && Number(userId) === currentUser?.id;
  const { data: fetchedUser } = useGetUserQuery(isNumericId ? Number(userId) : 0, {
    skip: !isNumericId || isCurrentUser,
  });
  const userData = isCurrentUser ? currentUser : fetchedUser;

  if (!userId) {
    return fallback;
  }

  if (!isNumericId) {
    return userId;
  }

  return userData
    ? `${capitalize(userData.first_name)} ${capitalize(userData.last_name)}`.trim()
    : userId;
};

export default UserDisplay;
